import { useEffect, useState } from 'react'
import Checkbox from 'components/Checkbox'

import './style.css'
import { TKnowledgeLevel, TRelevanceLevel } from 'pages/Planning/types'
import { KnowledgeSlider } from './KnowledgeSlider'
import { KNOWLEDGE_LEVELS } from 'pages/Planning/types'
import { RelevanceSlider } from './RelevanceSlider'
import { RELEVANCE_LEVELS } from 'pages/Planning/types'

interface DisciplinesSelectionProps {
  options: any[]
  selectedItems: any[]
  onSelect: (ref: string, checked: boolean) => void
  updateKnowledgeLevel: (ref: string, knowledgeLevel: TKnowledgeLevel) => void
  updateRelevanceLevel: (ref: string, relevanceLevel: TRelevanceLevel) => void
  onNext: (stepId: string) => void
  isDarkMode?: boolean
  loading?: boolean
}

const DisciplinesSelection = ({
  options,
  selectedItems,
  onSelect,
  updateKnowledgeLevel,
  updateRelevanceLevel,
  onNext,
  isDarkMode = true,
  loading = false,
}: DisciplinesSelectionProps) => {
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null)
  const [selectedAll, setSelectedAll] = useState(false)

  const DisciplinesSkeleton = ({ idx }: { idx: number }) => {
    return (
      <div className="disciplines-skeleton disciplines-option">
        <div className="disciplines-skeleton-item">
          <div className="disciplines-skeleton-item-checkbox"></div>
          <div className="disciplines-skeleton-item-subject"></div>
        </div>
      </div>
    )
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectedAll(checked)
    options.forEach((option) => {
      onSelect(option.ref, checked)
    })
  }

  const handleSubjectToggle = (ref: string, checked: boolean) => {
    onSelect(ref, checked)
    if (checked) {
      setExpandedSubject(ref)
    } else {
      if (expandedSubject === ref) setExpandedSubject(null)
    }
  }
  const handleKnowledgeLevelSelect = (ref: string, knowledgeLevel: number) => {
    updateKnowledgeLevel(ref, knowledgeLevel as TKnowledgeLevel)
  }

  const handleRelevanceLevelSelect = (ref: string, relevanceLevel: number) => {
    updateRelevanceLevel(ref, RELEVANCE_LEVELS[relevanceLevel - 1].value as TRelevanceLevel)
  }

  const isSelected = (ref: string) => selectedItems.some((s) => s.ref === ref)

  const handleEnableDiscipline = (ref: string) => {
    if (!isSelected(ref)) {
      onSelect(ref, true)
    }
  }
  const getKnowledgeLevel = (ref: string) => {
    const discipline = selectedItems.find((s) => s.ref === ref)
    return discipline?.knowledgeLevel ?? 0
  }
  const getRelevanceLevel = (ref: string) =>
    RELEVANCE_LEVELS.findIndex(
      (s) => s.value === selectedItems.find((s) => s.ref === ref)?.relevanceLevel
    ) + 1
  const getWeight = (ref: string) => selectedItems.find((s) => s.ref === ref)?.weight || 0

  return (
    <div className={`disciplines-selection ${isDarkMode ? 'dark' : 'light'}`}>
      <p className="disciplines-description">
        Selecione as disciplinas que deseja incluir, defina sua relevância no edital e o seu nível
        de conhecimento:
      </p>

      <div className="disciplines-selection-all">
        <Checkbox
          value={selectedAll}
          className="checkbox"
          onChange={(checked) => handleSelectAll(checked as boolean)}
        >
          Selecionar todas as disciplinas
        </Checkbox>
      </div>

      <div className="disciplines-options" data-tour="disciplines-list">
        {loading ? (
          Array.from({ length: 8 }, (_, idx) => (
            <DisciplinesSkeleton idx={idx} key={`disciplines-skeleton-${idx}`} />
          ))
        ) : (
          <>
            {options.map((option, index) => {
              const selected = isSelected(option.ref)
              const relevance = Math.max(1, getRelevanceLevel(option.ref))
              const knowledge = Math.max(0, getKnowledgeLevel(option.ref))
              const weight = getWeight(option.ref)
              // Always show data-tour on first option, even if not selected, so tour can find it
              const isFirstOption = index === 0

              return (
                <div
                  key={option.ref}
                  className={`disciplines-option ${selected ? 'selected' : ''}`}
                >
                  <div>
                    <Checkbox
                      value={selected}
                      className="checkbox"
                      onChange={(checked) => handleSubjectToggle(option.ref, checked as boolean)}
                    >
                      {option.subject}
                    </Checkbox>
                    <div
                      className="disciplines-option-level-container"
                      data-tour={isFirstOption ? 'disciplines-level' : undefined}
                    >
                      <div className="disciplines-option-level-options">
                        <RelevanceSlider
                          value={relevance}
                          onChange={(value) => handleRelevanceLevelSelect(option.ref, value)}
                          disabled={!selected}
                          onEnable={() => handleEnableDiscipline(option.ref)}
                        />
                        <KnowledgeSlider
                          value={knowledge}
                          onChange={(value) => handleKnowledgeLevelSelect(option.ref, value)}
                          disabled={!selected}
                          onEnable={() => handleEnableDiscipline(option.ref)}
                        />
                        <div className="disciplines-option-weight" data-tour="disciplines-weight">
                          <span className="disciplines-option-weight-label">Peso</span>
                          <span className="disciplines-option-weight-value">{weight}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>

      <div className="item-next-button">
        <button
          className="next-button"
          data-tour="wizard-next"
          onClick={() => onNext('step-3')}
          disabled={!selectedItems}
        >
          Próximo
        </button>
      </div>
    </div>
  )
}

export default DisciplinesSelection
